import { MutationObserver } from "@tanstack/query-core";
import type { CreateUnifiedLinkParams } from "api-contracts/rpc/create-unified-link";
import type { DeleteUnifiedLinkParams } from "api-contracts/rpc/delete-unified-link";
import type { GetUnifiedLinksResult } from "api-contracts/rpc/get-unified-links";
import type { UpdateUnifiedLinkParams } from "api-contracts/rpc/update-unified-link";
import type { UpdateUnifiedLinksOrderParams } from "api-contracts/rpc/update-unified-links-order";
import type { UpdateContentTierResult } from "api-contracts/rpc/update-content-tier";
import { queryClient } from "ui-common/data-query-controller";
import { rpc } from "ui-common/services/rpc";
import { NotificationService } from "ui-components/notification/notification-service";
import { isStripePaywallEnabled } from "../config/stripe-paywall-enabled-creators";
import { appContext } from "ui-common/appContext";

export const createUnifiedLink = new MutationObserver(queryClient, {
  mutationKey: ["createUnifiedLink"],
  mutationFn: async (params: CreateUnifiedLinkParams) => {
    const result = await rpc("createUnifiedLink", params);

    if (!result?.linkId) {
      return result;
    }

    // Handle premium tier if specified
    if (params.contentTier === "premium") {
      const tierResponse = (await rpc("update-content-tier", {
        contentType: "link",
        contentId: result.linkId,
        tier: "premium",
      })) as UpdateContentTierResult;

      // Show notification if subscription tier was auto-created with default price
      if (tierResponse?.tierCreated && tierResponse?.defaultPriceAssigned) {
        NotificationService.info(
          `Premium subscription enabled! Default price set to €${(tierResponse.defaultPriceAssigned / 100).toFixed(2)}/month. You can change this in Settings.`,
        );
      }
    }

    // Handle one-time purchase if specified
    if (params.oneTimePriceCents && params.contentTier === "premium") {
      await rpc("enable-one-time-purchase", {
        contentType: "link",
        contentId: result.linkId,
        priceCents: params.oneTimePriceCents,
        productName: `Premium Link - ${params.title.substring(0, 50)}`,
      });
    }

    return result;
  },
  onSuccess: () => queryClient.invalidateQueries({ queryKey: ["unifiedLinks"] }),
});

export const updateUnifiedLink = new MutationObserver(queryClient, {
  mutationKey: ["updateUnifiedLink"],
  mutationFn: async (params: UpdateUnifiedLinkParams) => {
    const result = await rpc("updateUnifiedLink", params);

    if (!result?.linkId) {
      return result;
    }

    // Handle premium tier if specified
    if (params.contentTier === "premium") {
      const tierResponse = (await rpc("update-content-tier", {
        contentType: "link",
        contentId: result.linkId,
        tier: "premium",
      })) as UpdateContentTierResult;

      // Show notification if subscription tier was auto-created with default price
      if (tierResponse?.tierCreated && tierResponse?.defaultPriceAssigned) {
        NotificationService.info(
          `Premium subscription enabled! Default price set to €${(tierResponse.defaultPriceAssigned / 100).toFixed(2)}/month. You can change this in Settings.`,
        );
      }
    } else if (params.contentTier === "free") {
      // Downgrade to free if specified
      await rpc("update-content-tier", {
        contentType: "link",
        contentId: result.linkId,
        tier: "free",
      });
    }

    if (!isStripePaywallEnabled(appContext.currentUser?.handle)) {
      return result;
    }

    // Handle one-time purchase
    if (params.oneTimePriceCents && params.contentTier === "premium") {
      await rpc("enable-one-time-purchase", {
        contentType: "link",
        contentId: result.linkId,
        priceCents: params.oneTimePriceCents,
        productName: `Premium Link - ${params.title.substring(0, 50)}`,
      });
    } else if (!params.oneTimePriceCents || params.contentTier === "free") {
      // Disable one-time purchase if not specified or downgraded to free
      await rpc("disable-one-time-purchase", {
        contentType: "link",
        contentId: result.linkId,
      });
    }

    return result;
  },
  onSuccess: () => queryClient.invalidateQueries({ queryKey: ["unifiedLinks"] }),
});

export const deleteUnifiedLink = new MutationObserver(queryClient, {
  mutationKey: ["deleteUnifiedLink"],
  mutationFn: (params: DeleteUnifiedLinkParams) => rpc("deleteUnifiedLink", params),
  onSuccess: () => queryClient.invalidateQueries({ queryKey: ["unifiedLinks"] }),
});

export const updateUnifiedLinksOrderMutation = new MutationObserver(queryClient, {
  mutationKey: ["updateUnifiecLinksOrder"],
  mutationFn: (params: UpdateUnifiedLinksOrderParams) => rpc("updateUnifiedLinksOrder", params),
  onMutate: async (variables) => {
    // Cancel any outgoing refetches so they don't overwrite our optimistic update
    await queryClient.cancelQueries({ queryKey: ["unifiedLinks"] });

    // Snapshot the previous value
    const previousLinks = queryClient.getQueryData(["unifiedLinks"]);

    // Optimistically update the cache
    queryClient.setQueryData(["unifiedLinks"], (old: GetUnifiedLinksResult | undefined) => {
      if (!old?.links) return old;

      // Reorder the links array based on the new order
      const reorderedLinks = variables.linkIds
        .map((linkId) => old.links.find((link) => link.id === linkId))
        .filter(Boolean); // Remove any undefined values

      return {
        ...old,
        links: reorderedLinks,
      };
    });

    // Return context object with the snapshot value
    return { previousLinks };
  },
  onError: (err, variables, context) => {
    // If the mutation fails, use the context returned from onMutate to roll back
    if (context?.previousLinks) {
      queryClient.setQueryData(["unifiedLinks"], context.previousLinks);
    }
  },
  onSettled: () => {
    // Always refetch after error or success to ensure we have fresh data
    queryClient.invalidateQueries({ queryKey: ["unifiedLinks"] });
  },
});
